import { useEffect, useRef } from 'react';
import { driver, DriveStep, Driver, Config } from 'driver.js';
import { TpiTourKey } from '@typeRoots/common/tourProgress';
import { useTourProgress } from './useTourProgress';
import { logger } from 'src/helpers/logger';
import { createTourError, handleTourError } from '../tourErrorHandler';
import { TOUR_ENABLED, TOUR_TIMING } from '../tourConfig';
import { applyTourTestIds } from '../utils/tourTestIds';

/**
 * Wait for tour elements to be present in the DOM
 * Polls for element availability with configurable timeout
 *
 * @param steps - Tour steps containing element selectors
 * @param maxWaitMs - Maximum time to wait for elements (default: 5000ms)
 * @returns Promise<boolean> - true if all elements found, false if timeout
 */
async function waitForTourElements(
  steps: DriveStep[],
  maxWaitMs: number = TOUR_TIMING.ELEMENT_READY_TIMEOUT_MS,
): Promise<boolean> {
  const startTime = Date.now();
  const elementsToFind = steps.map((step) => step.element).filter(Boolean);

  // If no elements to check, consider ready
  if (elementsToFind.length === 0) {
    return true;
  }

  while (Date.now() - startTime < maxWaitMs) {
    const allFound = elementsToFind.every((selector) => document.querySelector(selector as string));

    if (allFound) {
      return true;
    }

    // Wait before checking again
    await new Promise((resolve) => setTimeout(resolve, TOUR_TIMING.ELEMENT_CHECK_INTERVAL_MS));
  }

  logger.warn('Tour elements not ready within timeout', {
    elementsToFind,
    maxWaitMs,
  });
  return false;
}

interface UseTourAutoStartOptions {
  /** Tour key to track completion state */
  tourKey: TpiTourKey;

  /** Driver.js step configuration */
  driverSteps: DriveStep[];

  /** Whether the tour should be enabled (use for conditional logic) */
  enabled?: boolean;

  /** Callback when tour completes successfully */
  onComplete?: () => void;

  /** Additional driver.js configuration options */
  driverConfig?: Partial<Config>;
}

/**
 * Auto-start Tour Hook
 * Automatically shows a tour on first visit to a screen
 *
 * @example
 * useTourAutoStart({
 *   tourKey: 'companyLanding',
 *   driverSteps: getCompanyLandingTour(),
 *   enabled: !!company && !isLoading,
 * });
 */
export function useTourAutoStart({
  tourKey,
  driverSteps,
  enabled = true,
  onComplete,
  driverConfig = {},
}: UseTourAutoStartOptions) {
  const { isCompleted, markCompleted, isLoading } = useTourProgress();
  const driverRef = useRef<Driver | null>(null);
  const hasAttemptedStart = useRef(false);
  const stepsCompleted = useRef(0);

  useEffect(() => {
    // Don't start if:
    // - Already started once
    // - Not enabled
    // - Tour already completed
    // - Still loading tour progress
    if (
      !TOUR_ENABLED ||
      hasAttemptedStart.current ||
      !enabled ||
      isCompleted(tourKey) ||
      isLoading
    ) {
      return;
    }

    // Mark that we've attempted to start
    hasAttemptedStart.current = true;
    // Reset step completion counter
    stepsCompleted.current = 0;

    // Create driver instance with default config + user overrides
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      allowClose: true,
      popoverClass: 'tpi-tour-popover',
      steps: driverSteps,
      onPopoverRender: applyTourTestIds,

      // Track step progression to detect natural completion
      onNextClick: () => {
        stepsCompleted.current += 1;
        // Must call moveNext() to actually advance the tour
        driverObj.moveNext();
      },

      // Only mark as completed if user clicked through all steps naturally
      onDestroyed: () => {
        // User completed the tour if they clicked "Next" through all steps
        const completedNaturally = stepsCompleted.current >= driverSteps.length;

        if (completedNaturally) {
          // User completed the tour by clicking through all steps
          markCompleted(tourKey);

          if (onComplete) {
            onComplete();
          }
        }
      },

      ...driverConfig,
    });

    driverRef.current = driverObj;

    // Wait for DOM elements to be ready, then start tour
    const startTourWhenReady = async () => {
      try {
        const elementsReady = await waitForTourElements(driverSteps);

        if (!elementsReady) {
          const tourError = createTourError(
            'TOUR_ELEMENT_NOT_FOUND',
            tourKey,
            new Error('Elements not found'),
            {
              shouldNotifyUser: false, // Don't spam user for auto-start failures
            },
          );
          handleTourError(tourError);
          return;
        }

        driverObj.drive();
        logger.debug(`Starting tour: "${tourKey}"`);
      } catch (error) {
        const tourError = createTourError('TOUR_START_FAILED', tourKey, error, {
          shouldNotifyUser: false,
        });
        handleTourError(tourError);
      }
    };

    // Small initial delay to let React Native Web render
    const timeoutId = setTimeout(() => {
      startTourWhenReady();
    }, TOUR_TIMING.AUTO_START_DELAY_MS);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: Intentionally omitting driverSteps, driverConfig, and onComplete from deps
    // These are new references on every render but don't represent meaningful changes
    // that should retrigger the tour. We only want to start the tour once per mount.
  }, [tourKey, enabled, isCompleted, isLoading, markCompleted]);

  return {
    /** Manually start the tour (useful for "?" button) */
    startTour: () => {
      if (driverRef.current) {
        driverRef.current.drive();
      }
    },

    /** Stop the tour */
    stopTour: () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    },
  };
}
